<template>
  <div class="pricing-matrix-container p-16 bg-white rounded-sm shadow-sm">
    <div class="d-flex justify-content-between align-items-center mb-16">
      <div class="d-flex align-items-center gap-16">
        <h5 class="mb-0">{{ $t('template.pricingMatrix') }}</h5>
        <div class="d-flex align-items-center gap-8">
          <div v-for="y in availableYears" :key="y" 
            class="year-pill cursor-pointer px-12 py-4 rounded-pill fw-bold text-xs transition-all"
            :class="selectedYear == y ? 'bg-primary text-white shadow-sm' : 'bg-light text-muted border border-light'"
            @click="selectedYear = y">
            {{ y }}
          </div>
        </div>
      </div>
      <v-button
        :is-loading="saving"
        size="sm"
        variant="primary"
        @click="savePricing"
      >
        {{ $t('general.button.save') }}
        <v-icon icon="arrow-right" size="xs" space="ms-8" />
      </v-button>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered table-sm align-middle pricing-table">
        <thead class="bg-light">
          <tr>
            <th class="text-center bg-light sticky-col" style="min-width: 120px;">{{ $t('general.shared.month') }}</th>
            <th v-for="roomType in ROOM_TYPES" :key="roomType.value" class="text-center">
              {{ $t(roomType.label) }} ({{ $t('template.pricePerPerson') }})
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="month in 12" :key="month">
            <td class="text-center fw-bold bg-light sticky-col month-cell">
              {{ getMonthName(month) }}
            </td>
            <td v-for="roomType in ROOM_TYPES" :key="roomType.value" class="p-0">
              <div class="price-input-wrapper d-flex align-items-center justify-content-center">
                <input
                  v-model="matrix[month][roomType.value]"
                  type="number"
                  step="0.01"
                  min="0"
                  class="form-control form-control-sm border-0 text-end no-arrows pe-8 w-100"
                  placeholder="0.00"
                  @paste="handlePaste($event, month, roomType.value)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, reactive, onMounted, watch, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useToast, POSITION } from "vue-toastification";
import { useRoute, useRouter } from "vue-router";
import Template from "@/api/objects/Template";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import api from "@/api";

import { useAuthStore } from "@/stores/auth";

const props = defineProps({
  template: {
    type: Object as () => Template,
    required: true,
  },
  agency: {
    type: String,
    required: false,
    default: "",
  },
});

const emit = defineEmits(["reloadTemplate"]);
const { t, locale } = useI18n();
const toast = useToast();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const saving = ref(false);

const availableYears = computed(() => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];
});

const selectedYear = ref<number>(new Date().getFullYear());

// Sync with URL
onMounted(() => {
  if (route.query.year) {
    selectedYear.value = parseInt(route.query.year as string, 10);
  }
});

watch(selectedYear, (newYear) => {
  router.push({ query: { ...route.query, year: newYear.toString() } });
  loadPricing();
});

const ROOM_TYPES = [
  { value: "single_room", label: "supplier.singleRoom" },
  { value: "double_room", label: "supplier.doubleRoom" },
  { value: "double_twin_room", label: "supplier.doubleTwinRoom" },
  { value: "triple_room", label: "supplier.tripleRoom" },
];

// Initialize matrix with months (1-12) and room types
const matrix = reactive<Record<number, Record<string, string>>>({});
const resetMatrix = () => {
  for (let m = 1; m <= 12; m++) {
    matrix[m] = {};
    ROOM_TYPES.forEach((rt) => {
      matrix[m][rt.value] = "";
    });
  }
};

resetMatrix();

const getMonthName = (month: number) => {
  const date = new Date(2000, month - 1, 1);
  return date.toLocaleString(locale.value, { month: "long" });
};

const handlePaste = (event: ClipboardEvent, startMonth: number, startRoomType: string) => {
  const clipboardData = event.clipboardData;
  if (!clipboardData) return;

  const pastedText = clipboardData.getData("text");
  if (!pastedText) return;

  // Split by newlines for rows, then by tabs for columns (standard Excel copy format)
  const rows = pastedText.split(/\r?\n/).filter((row) => row.trim() !== "");
  if (rows.length === 0) return;

  event.preventDefault();

  const startRoomTypeIndex = ROOM_TYPES.findIndex((rt) => rt.value === startRoomType);

  rows.forEach((row, rowIndex) => {
    const columns = row.split("\t");
    const targetMonth = startMonth + rowIndex;

    // Check if month is within bounds (1-12)
    if (targetMonth > 12) return;

    columns.forEach((colValue, colIndex) => {
      const targetRoomTypeIndex = startRoomTypeIndex + colIndex;

      // Check if room type index is within bounds
      if (targetRoomTypeIndex >= ROOM_TYPES.length) return;

      const targetRoomType = ROOM_TYPES[targetRoomTypeIndex].value;

      // Clean and parse the pasted value
      // Handle European formats where comma is used as decimal separator
      const cleanValue = colValue.trim().replace(",", ".");
      
      // If it's a valid number (or empty), update the matrix
      if (cleanValue === "" || !isNaN(parseFloat(cleanValue))) {
        matrix[targetMonth][targetRoomType] = cleanValue;
      }
    });
  });
};

const loadPricing = () => {
  resetMatrix();
  const prices = (props.template as any)?.pricing_matrix || (props.template as any)?.pricingMatrix || [];
  prices.forEach((p: any) => {
    if (
      p.room_type && 
      p.month && 
      p.year == selectedYear.value && 
      matrix[p.month] && 
      matrix[p.month][p.room_type] !== undefined
    ) {
        matrix[p.month][p.room_type] = p.price != null ? String(p.price) : "";
    }
  });
};

const savePricing = async () => {
  saving.value = true;
  try {
    const pricing_matrix: any[] = [];
    
    // Add monthly prices from matrix for the selected year
    for (let m = 1; m <= 12; m++) {
      ROOM_TYPES.forEach((rt) => {
        const priceValue = matrix[m][rt.value];
        if (priceValue !== "" && priceValue !== null) {
          pricing_matrix.push({
            room_type: rt.value,
            month: m,
            year: selectedYear.value,
            price: parseFloat(priceValue) || 0,
          });
        }
      });
    }

    // We only need to send what the update method expects
    const payload = {
        name: props.template.name,
        travel_type: props.template.travelType,
        pricing_matrix,
    };

    const agencySlug = props.agency || authStore.agency?.slug || "";

    await api.activityService.updateActivity(agencySlug, props.template.id, payload);
    
    toast.success(t("general.shared.savedChanges"), { position: POSITION.BOTTOM_RIGHT });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || "An error occurred.");
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  loadPricing();
});

watch(
  () => props.template,
  () => loadPricing(),
  { deep: true }
);
</script>

<style scoped>
.pricing-matrix-container {
  max-width: 100%;
}
.pricing-table th, .pricing-table td.month-cell {
  padding: 12px 8px;
  vertical-align: middle;
}
.pricing-table td.p-0 {
  padding: 0 !important;
}
.pricing-table .form-control {
  border-radius: 0;
  box-shadow: none;
  height: 42px; /* Slightly taller to match the 12px padding of other cells */
}
.pricing-table .form-control:focus {
  background-color: #f8f9fa;
  z-index: 1;
}
.year-pill {
  border: 1.5px solid transparent;
  min-width: 60px;
  text-align: center;
}
.year-pill:hover {
  background-color: #f1f3f5;
  color: #fd0c6f !important;
}
.year-pill.bg-primary:hover {
  background-color: #dc095f !important;
  color: #ffffff !important;
}
.transition-all {
  transition: all 0.2s ease-in-out;
}
.cursor-pointer {
  cursor: pointer;
}
.price-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  border-right: 2px solid #dee2e6 !important;
}
.no-arrows::-webkit-outer-spin-button,
.no-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.no-arrows {
  -moz-appearance: textfield;
}
</style>
